import { useState, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { api } from '../../api/client';
import { Search, X, Check, Paperclip, Loader, Image as ImageIcon, File as FileIcon } from 'lucide-react';

export default function CreateTicketModal() {
    const { showCreateTicket, setShowCreateTicket, createTicket, users, departments, settings, projects, activeView } = useApp();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('medium');
    const [itemType, setItemType] = useState('ticket');
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [estimateHours, setEstimateHours] = useState('');
    const [projectId, setProjectId] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
    const [collaboratorIds, setCollaboratorIds] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [pendingAttachments, setPendingAttachments] = useState<any[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Search and Dropdown states
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
    const [collaboratorMenuOpen, setCollaboratorMenuOpen] = useState(false);
    const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);

    const assigneeRef = useRef<HTMLDivElement>(null);
    const collaboratorRef = useRef<HTMLDivElement>(null);
    const priorityRef = useRef<HTMLDivElement>(null);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setPriority('medium');
        setItemType('ticket');
        setStartDate('');
        setDueDate('');
        setEstimateHours('');
        setProjectId('');
        setSectionId('');
        setDepartmentId('');
        setAssigneeIds([]);
        setCollaboratorIds([]);
        setAssigneeSearch('');
        setCollaboratorSearch('');
        setPendingAttachments([]);
        setError('');
    };

    useEffect(() => {
        if (!showCreateTicket) return;
        const activeProject = (activeView.type === 'ticket-board' || activeView.type === 'project-list')
            ? projects.find(project => project.id === activeView.id && project.permissions.can_edit_items)
            : undefined;
        setProjectId(activeProject?.id || '');
        setSectionId(activeProject?.sections[0]?.id || '');
    }, [showCreateTicket, activeView.type, activeView.id, projects]);

    const handleClose = () => {
        setShowCreateTicket(false);
        resetForm();
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (assigneeRef.current && !assigneeRef.current.contains(event.target as Node)) {
                setAssigneeMenuOpen(false);
            }
            if (collaboratorRef.current && !collaboratorRef.current.contains(event.target as Node)) {
                setCollaboratorMenuOpen(false);
            }
            if (priorityRef.current && !priorityRef.current.contains(event.target as Node)) {
                setPriorityMenuOpen(false);
            }
        }
        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                if (assigneeMenuOpen || collaboratorMenuOpen || priorityMenuOpen) {
                    setAssigneeMenuOpen(false);
                    setCollaboratorMenuOpen(false);
                    setPriorityMenuOpen(false);
                } else {
                    handleClose();
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [assigneeMenuOpen, collaboratorMenuOpen, priorityMenuOpen, showCreateTicket, handleClose]);

    if (!showCreateTicket) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;

        const selectedDept = departments.find(d => d.id === departmentId);
        const deptBlocksIndividual = selectedDept && selectedDept.allow_external_individual_assignment === false;

        setIsSubmitting(true);
        try {
            setError('');
            await createTicket({
                title,
                description,
                priority: priority as any,
                department_id: departmentId || null,
                assigned_to_department_id: deptBlocksIndividual ? departmentId : null,
                assignee_ids: deptBlocksIndividual ? [] : assigneeIds,
                collaborator_ids: collaboratorIds,
                project_id: projectId || null,
                section_id: sectionId || null,
                item_type: itemType as any,
                start_date: startDate ? new Date(`${startDate}T12:00:00`).toISOString() : null,
                due_date: dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : null,
                estimate_minutes: estimateHours ? Math.round(Number(estimateHours) * 60) : null,
                is_milestone: itemType === 'milestone',
                attachments: pendingAttachments.filter(a => !a.uploading)
            });
            handleClose();
        } catch (err: any) {
            console.error('Failed to create ticket:', err);
            setError(err.message || 'Failed to create ticket');
        }
        setIsSubmitting(false);
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        for (const file of files) {
            const placeholderId = Math.random().toString();
            try {
                const placeholder = { id: placeholderId, original_name: file.name, uploading: true };
                setPendingAttachments(prev => [...prev, placeholder]);

                const uploaded = await api.uploadFile(file);

                setPendingAttachments(prev => prev.map(p => p.id === placeholderId ? uploaded : p));
            } catch (err) {
                console.error("Upload failed", err);
                setPendingAttachments(prev => prev.filter(p => p.id !== placeholderId));
                setError("Failed to upload " + file.name);
            }
        }
    };

    const handlePaste = async (e: React.ClipboardEvent) => {
        if (!e.clipboardData) return;
        const items = Array.from(e.clipboardData.items);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        if (!imageItem) return;

        e.preventDefault();
        const blob = imageItem.getAsFile();
        if (!blob) return;

        const ext = imageItem.type.split('/')[1] || 'png';
        const fileName = `paste-${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: imageItem.type });

        const placeholderId = Math.random().toString();
        try {
            setPendingAttachments(prev => [...prev, { id: placeholderId, original_name: fileName, uploading: true }]);
            const uploaded = await api.uploadFile(file);
            setPendingAttachments(prev => prev.map(p => p.id === placeholderId ? uploaded : p));
        } catch (err) {
            console.error("Paste upload failed", err);
            setPendingAttachments(prev => prev.filter(p => p.id !== placeholderId));
            setError("Failed to upload pasted image");
        }
    };


    const toggleAssignee = (userId: string, isCtrlKey: boolean) => {
        setAssigneeIds(prev => {
            const isSelected = prev.includes(userId);
            if (isSelected) {
                return prev.filter(id => id !== userId);
            } else {
                if (settings?.allow_multiple_assignees && isCtrlKey) {
                    // Allow up to 2 if Ctrl is held
                    if (prev.length < 2) {
                        return [...prev, userId];
                    }
                    return prev;
                } else {
                    // Single select if Ctrl is not held
                    return [userId];
                }
            }
        });
    };

    const toggleCollaborator = (userId: string) => {
        setCollaboratorIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const getInitials = (name: string) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
    };

    const filteredUsers = (search: string) => {
        if (!search.trim()) return [];
        return users.filter(user => user.name.toLowerCase().includes(search.toLowerCase()));
    };

    const selectedProject = projects.find(project => project.id === projectId);

    const UserSearchDropdown = ({
        label,
        selectedIds,
        onSelect,
        search,
        setSearch,
        isOpen,
        setIsOpen,
        dropdownRef,
        isAssignee = false
    }: any) => (
        <div className="form-group" ref={dropdownRef} style={{ position: 'relative' }}>
            <label className="form-label">{label}</label>
            <div
                className="input"
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    minHeight: 38,
                    alignItems: 'center',
                    cursor: 'text',
                    padding: '4px 32px 4px 12px',
                    position: 'relative'
                }}
            >
                {selectedIds.length === 0 && !isOpen && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Select users...</span>
                )}
                {selectedIds.map((id: string) => {
                    const user = users.find(u => u.id === id);
                    if (!user) return null;
                    return (
                        <span
                            key={id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                background: 'var(--accent-primary)',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: 12,
                                fontSize: 12
                            }}
                        >
                            {user.name}
                            <X
                                size={12}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(id, false);
                                }}
                            />
                        </span>
                    );
                })}
                {isOpen && (
                    <input
                        autoFocus
                        style={{ border: 'none', outline: 'none', background: 'transparent', color: 'inherit', fontSize: 13, flex: 1, minWidth: 60 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search..."
                    />
                )}
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.5 }}>
                    <Search size={16} />
                </div>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 8,
                    marginTop: 4,
                    maxHeight: 200,
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: 'var(--shadow-lg)'
                }}>
                    {!search.trim() ? (
                        <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>Type to search for users...</div>
                    ) : filteredUsers(search).length === 0 ? (
                        <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: 13 }}>No users found</div>
                    ) : (
                        filteredUsers(search).map(user => {
                            const isSelected = selectedIds.includes(user.id);
                            return (
                                <div
                                    key={user.id}
                                    onClick={(e) => {
                                        const multiSelect = isAssignee && e.ctrlKey;
                                        onSelect(user.id, multiSelect);
                                        setSearch('');
                                        if (!multiSelect) {
                                            setIsOpen(false);
                                        }
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        background: isSelected ? 'var(--bg-tertiary)' : 'transparent',
                                        transition: 'background 0.2s',
                                        fontSize: 13
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = isSelected ? 'var(--bg-tertiary)' : 'transparent')}
                                >
                                    <div className="avatar avatar-sm" style={{ width: 24, height: 24, fontSize: 11 }}>
                                        {getInitials(user.name)}
                                    </div>
                                    <span style={{ flex: 1 }}>{user.name}</span>
                                    {isSelected && <Check size={14} color="var(--accent-primary)" />}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
            {isAssignee && isOpen && settings?.allow_multiple_assignees && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                    Tip: Hold Ctrl to select up to 2 assignees.
                </div>
            )}
        </div>
    );

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                <header className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(79, 70, 229, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
                            <ClipboardList size={20} />
                        </div>
                        <h2 className="modal-title">Create a ticket</h2>
                    </div>
                    <button className="btn btn-ghost btn-icon" onClick={handleClose}>✕</button>
                </header>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {error && (
                        <div className="auth-error" style={{ margin: 'var(--spacing-md) var(--spacing-md) 0' }}>
                            {error}
                        </div>
                    )}
                    <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-project">Project</label>
                                <select id="ticket-project" className="input select" value={projectId} onChange={event => {
                                    const nextProject = projects.find(project => project.id === event.target.value);
                                    setProjectId(event.target.value);
                                    setSectionId(nextProject?.sections[0]?.id || '');
                                }}>
                                    <option value="">Support / unscoped</option>
                                    {projects.filter(project => project.permissions.can_edit_items).map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-section">Section</label>
                                <select id="ticket-section" className="input select" value={sectionId} disabled={!selectedProject} onChange={event => setSectionId(event.target.value)}>
                                    <option value="">No section</option>
                                    {selectedProject?.sections.map(section => <option key={section.id} value={section.id}>{section.name}</option>)}
                                </select>
                            </div>
                        </div>
                        {/* Title */}
                        <div className="form-group">
                            <label className="form-label">Title *</label>
                            <input
                                type="text"
                                className="input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Brief summary of the issue"
                                autoFocus
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--spacing-md)' }}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-type">Type</label>
                                <select id="ticket-type" className="input select" value={itemType} onChange={event => setItemType(event.target.value)}>
                                    <option value="ticket">Ticket</option><option value="task">Task</option><option value="bug">Bug</option>
                                    <option value="request">Request</option><option value="incident">Incident</option><option value="approval">Approval</option><option value="milestone">Milestone</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-start">Start</label>
                                <input id="ticket-start" type="date" className="input" value={startDate} onChange={event => setStartDate(event.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-due">Due</label>
                                <input id="ticket-due" type="date" className="input" value={dueDate} min={startDate || undefined} onChange={event => setDueDate(event.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label" htmlFor="ticket-estimate">Estimate (h)</label>
                                <input id="ticket-estimate" type="number" className="input" min="0" step="0.25" value={estimateHours} onChange={event => setEstimateHours(event.target.value)} placeholder="0" />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea
                                className="input textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                onPaste={handlePaste}
                                placeholder="Provide more details about the issue..."
                                rows={4}
                            />
                        </div>

                        {/* Priority & Department row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                            <div className="form-group" style={{ position: 'relative' }} ref={priorityRef}>
                                <label className="form-label">Priority</label>
                                <div
                                    className="input select"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                    onClick={() => setPriorityMenuOpen(!priorityMenuOpen)}
                                >
                                    {priority === 'low' && <><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6' }} /> Low</>}
                                    {priority === 'medium' && <><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981' }} /> Medium</>}
                                    {priority === 'high' && <><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} /> High</>}
                                    {priority === 'urgent' && <><div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} /> Urgent</>}
                                    <div style={{ marginLeft: 'auto', opacity: 0.5 }}>▼</div>
                                </div>
                                {priorityMenuOpen && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                        background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                                        borderRadius: 8, marginTop: 4, zIndex: 1000, boxShadow: 'var(--shadow-lg)',
                                        padding: 4
                                    }}>
                                        {['low', 'medium', 'high', 'urgent'].map(p => (
                                            <div
                                                key={p}
                                                onClick={() => { setPriority(p); setPriorityMenuOpen(false); }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                                                    cursor: 'pointer', borderRadius: 4, fontSize: 13,
                                                    background: priority === p ? 'var(--bg-tertiary)' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = priority === p ? 'var(--bg-tertiary)' : 'transparent')}
                                            >
                                                <div style={{
                                                    width: 10, height: 10, borderRadius: '50%',
                                                    background: p === 'low' ? '#3b82f6' : p === 'medium' ? '#10b981' : p === 'high' ? '#f59e0b' : '#ef4444'
                                                }} />
                                                <span style={{ textTransform: 'capitalize' }}>{p}</span>
                                                {priority === p && <Check size={14} color="var(--accent-primary)" style={{ marginLeft: 'auto' }} />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Department</label>
                                <select
                                    className="input select"
                                    value={departmentId}
                                    onChange={(e) => setDepartmentId(e.target.value)}
                                >
                                    <option value="">Select department...</option>
                                    {departments.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Derived: selected dept object */}
                        {(() => {
                            const selectedDept = departments.find(d => d.id === departmentId);
                            const deptBlocksIndividual = selectedDept && selectedDept.allow_external_individual_assignment === false;
                            return deptBlocksIndividual ? (
                                <div className="form-group">
                                    <label className="form-label">Assign to</label>
                                    <div style={{
                                        padding: '10px 14px',
                                        background: 'var(--bg-tertiary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 8,
                                        fontSize: 13,
                                        color: 'var(--text-muted)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <span>🔒</span>
                                        <span>This department only accepts queue-based assignment — ticket will go to department head.</span>
                                    </div>
                                </div>
                            ) : null;
                        })()}

                        {/* Assignees — hidden if dept blocks individual assignment */}
                        {!departments.find(d => d.id === departmentId && d.allow_external_individual_assignment === false) && (
                            <UserSearchDropdown
                                label="Assign to"
                                selectedIds={assigneeIds}
                                onSelect={toggleAssignee}
                                search={assigneeSearch}
                                setSearch={setAssigneeSearch}
                                isOpen={assigneeMenuOpen}
                                setIsOpen={setAssigneeMenuOpen}
                                dropdownRef={assigneeRef}
                                isAssignee={true}
                            />
                        )}

                        {/* Collaborators */}
                        <UserSearchDropdown
                            label="Collaborators"
                            selectedIds={collaboratorIds}
                            onSelect={(id: string) => toggleCollaborator(id)}
                            search={collaboratorSearch}
                            setSearch={setCollaboratorSearch}
                            isOpen={collaboratorMenuOpen}
                            setIsOpen={setCollaboratorMenuOpen}
                            dropdownRef={collaboratorRef}
                        />

                        {/* Attachments UI (Previews inside body) */}
                        {pendingAttachments.length > 0 && (
                            <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
                                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    Attachments
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                                    {pendingAttachments.map((att, i) => (
                                        <div key={att.id || i} style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            background: 'var(--bg-tertiary)', padding: '6px 12px',
                                            borderRadius: 6, fontSize: 13, border: '1px solid var(--border-color)',
                                            opacity: att.uploading ? 0.6 : 1
                                        }}>
                                            {att.uploading ? <Loader size={14} className="spin" /> : (att.mime_type?.startsWith('image/') ? <ImageIcon size={14} /> : <FileIcon size={14} />)}
                                            <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {att.original_name}
                                            </span>
                                            {!att.uploading && (
                                                <X size={14}
                                                    style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                                                    onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </div>

                    <div className="modal-footer" style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => fileInputRef.current?.click()}
                                style={{ fontSize: 13, gap: 6, display: 'flex', alignItems: 'center', padding: '6px 12px', height: 'auto' }}
                            >
                                <Paperclip size={14} /> Attach File
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                            <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={!title.trim() || isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Ticket'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Helper to use ClipboardList icon if available in scope
import { ClipboardList } from 'lucide-react';

